import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import { RadioGroup } from '../../../../components/RadioGroup';
import { ConfigurationSection } from '../../../../containers/ConfigurationSection';
import type {
  QuickBooksConfigurationControl,
  QuickBooksConfigurationFormValues,
} from '../../QuickBooksConfigurationFormSchema';
import {
  AUTOMATICALLY_SYNC_INVOICES_OPTIONS,
  EXPORT_BANK_DETAILS_OPTIONS,
  INVOICE_NUMBER_SYNC_STRATEGY_OPTIONS,
  INVOICE_PAYMENT_STATUS_SYNC_STRATEGY_OPTIONS,
} from './constants';
import { getFlagFromStatus, getStatusByFlag } from '../../../../types/boolean-flag';

type InvoicesConfigurationSectionProps = {
  control: QuickBooksConfigurationControl;
  defaultValues: QuickBooksConfigurationFormValues;
};

export const InvoicesConfigurationSection: FC<InvoicesConfigurationSectionProps> = ({
  control,
  defaultValues,
}) => (
  <ConfigurationSection title="Invoices" iconName="InvoiceListItem">
    <Controller
      name="syncApprovedInvoices"
      control={control}
      defaultValue={defaultValues.syncApprovedInvoices}
      render={({ field: { value, onChange } }) => (
        <RadioGroup
          title="Choose how to sync approved invoices"
          value={getFlagFromStatus(value)}
          onChange={(selected) => onChange(getStatusByFlag(selected))}
          keyExtractor={(option) => option.value}
          options={AUTOMATICALLY_SYNC_INVOICES_OPTIONS}
        />
      )}
    />
    <Controller
      name="invoiceNumberSyncStrategy"
      control={control}
      defaultValue={defaultValues.invoiceNumberSyncStrategy}
      render={({ field: { value, onChange } }) => (
        <RadioGroup
          title="Choose how invoice numbers are managed"
          value={value}
          onChange={onChange}
          keyExtractor={(option) => option.value}
          options={INVOICE_NUMBER_SYNC_STRATEGY_OPTIONS}
        />
      )}
    />
    <Controller
      name="invoicePaymentStatusSyncStrategy"
      control={control}
      defaultValue={defaultValues.invoicePaymentStatusSyncStrategy}
      render={({ field }) => (
        <RadioGroup
          title="Choose how invoice payment status is managed"
          {...field}
          keyExtractor={(option) => option.value}
          options={INVOICE_PAYMENT_STATUS_SYNC_STRATEGY_OPTIONS}
        />
      )}
    />
    <Controller
      name="exportBankDetails"
      control={control}
      defaultValue={defaultValues.exportBankDetails}
      render={({ field: { value, onChange } }) => (
        <RadioGroup
          title="Choose whether to include bank details in QuickBooks invoices"
          value={getFlagFromStatus(value)}
          onChange={(selected) => onChange(getStatusByFlag(selected))}
          keyExtractor={(option) => option.value}
          options={EXPORT_BANK_DETAILS_OPTIONS}
        />
      )}
    />
  </ConfigurationSection>
);
